import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import logo from '../../Assets/sinFondo.png';
import {
  fetchRentalDetail,
  fetchRentalPayments,
  registerPayment,
  generatePaymentSchedule,
  simulateRentalAdjustment,
  fetchAdminRentalDetail,
  fetchAdminRentalPayments,
  registerAdminRentalPayment,
  generateAdminPaymentSchedule
} from '../../Redux/Actions/actions';
import {
  RENTAL_TYPE,
  useRentalType,
  getDetailPath,
  typeLabel
} from '../RentalManagement/utils/rentalTypeHelpers';
import { toast } from 'react-toastify';
import { 
  FaDollarSign, 
  FaCalendarAlt, 
  FaCheckCircle,
  FaExclamationTriangle,
  FaClock,
  FaArrowLeft,
  FaPlus,
  FaDownload,
  FaFilter 
} from 'react-icons/fa';
import style from './RentalPayments.module.css';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const RECEIPT_MM = { w: 170, h: 220 };

const RentalPayments = () => {
  const { contractId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const type = useRentalType();
  const isAdmin = type === RENTAL_TYPE.ADMIN;
  
  // Selector específico
  const rentalState = useSelector(state => state);
  
  const {
    rentalDetail,
    rentalPayments,
    adminRentalDetail,
    adminRentalPayments,
    rentalsLoading,
    adminRentalsLoading,
    rentalsError,
    adminRentalsError
  } = rentalState || {};
  
  const loading = isAdmin ? adminRentalsLoading : rentalsLoading;
  const error = isAdmin ? adminRentalsError : rentalsError;
  const detailSrc = isAdmin ? adminRentalDetail : rentalDetail;
  const paymentsSrc = isAdmin ? adminRentalPayments : rentalPayments;

  const [rental, setRental] = useState(null);
  const [payments, setPayments] = useState([]);
  const [contractInfo, setContractInfo] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showAdjustmentSimulator, setShowAdjustmentSimulator] = useState(false);
  const [simulation, setSimulation] = useState(null);
  const [adjustmentData, setAdjustmentData] = useState({
    adjustmentType: 'ICL',
    percentage: '',
    indexMonth: '',
    indexYear: new Date().getFullYear(),
    newAmount: ''
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amountPaid: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'Transferencia',
    receiptNumber: '',
    notes: ''
  });

  // Recibo (vista previa + descarga PDF)
  const receiptRef = useRef(null);
  const receiptPreviewViewportRef = useRef(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [receiptData, setReceiptData] = useState(null);
  const [receiptTenantName, setReceiptTenantName] = useState('');
  const isDev = process.env.NODE_ENV !== 'production';
  const [receiptDevMode, setReceiptDevMode] = useState(false);
  const [receiptPreviewScale, setReceiptPreviewScale] = useState(1);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAdminRentalDetail(contractId));
      dispatch(fetchAdminRentalPayments(contractId));
    } else {
      dispatch(fetchRentalDetail(contractId));
      dispatch(fetchRentalPayments(contractId));
    }
  }, [dispatch, contractId, isAdmin]);

  useEffect(() => {
    if (!detailSrc) return;
    if (isAdmin) {
      // Admin detail viene flat; mapeamos a algo que el render espera (campos en root del rental).
      const adm = detailSrc;
      setRental({
        // Campos de propiedad embebidos (con fallback a property)
        street: adm.property?.street || adm.propertyStreet || '',
        number: adm.property?.number || adm.propertyNumber || '',
        locality: adm.property?.locality || adm.propertyLocality || '',
        province: adm.property?.province || '',
        // Campos de contrato
        ...adm,
        contractStart: adm.contractStart,
        contractEnd: adm.contractEnd,
        currentRentValue: adm.currentRentValue,
        rentValue: adm.rentValue,
        currency: adm.currency,
        expensesAmount: adm.expensesAmount,
        adjustmentFrequency: adm.adjustmentFrequency
      });
    } else {
      setRental(detailSrc.rental || detailSrc);
    }
  }, [detailSrc, isAdmin, error]);

  useEffect(() => {
    const src = paymentsSrc;
    const normalize = (list) =>
      (Array.isArray(list) ? list : []).map((p) =>
        isAdmin && p && !p.paymentStatus
          ? { ...p, paymentStatus: p.status || p.paymentStatus || 'pending' }
          : p
      );

    if (src && typeof src === 'object') {
      if (Array.isArray(src.data)) {
        setPayments(normalize(src.data));
        if (src.contractInfo) setContractInfo(src.contractInfo);
        if (isAdmin && src.autoGenerated) {
          toast.info('Cronograma generado automáticamente.');
        }
      } else if (Array.isArray(src)) {
        setPayments(normalize(src));
      }
    } else if (Array.isArray(src) && src.length > 0) {
      setPayments(normalize(src));
    } else {
      setPayments([]);
    }
  }, [paymentsSrc, isAdmin]);

  // Función para determinar si una cuota es de ajuste
  const isAdjustmentMonth = (paymentNumber, contractStart, adjustmentFrequency) => {
    if (!contractStart || !adjustmentFrequency) return false;
    
    // El primer ajuste ocurre DESPUÉS de la frecuencia especificada
    // Si frequency = 3: ---+---+---+ (ajustes en meses 4, 8, 12...)
    // Si frequency = 2: --+--+--+ (ajustes en meses 3, 6, 9...)
    // Fórmula: el ajuste ocurre cada (frequency + 1) meses
    return paymentNumber > 0 && paymentNumber % (adjustmentFrequency + 1) === 0;
  };

  // Función para calcular el porcentaje de aumento
  const calculateAdjustmentPercentage = (originalAmount, paidAmount) => {
    if (!originalAmount || !paidAmount) return 0;
    const original = parseFloat(originalAmount);
    const paid = parseFloat(paidAmount);
    if (original === 0) return 0;
    return ((paid - original) / original * 100).toFixed(2);
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    
    if (!selectedPayment) {
      alert('Por favor selecciona una cuota para pagar');
      return;
    }

    try {
      const paymentData = {
        ...paymentForm,
        amountPaid: parseFloat(paymentForm.amountPaid),
        receiptNumber: paymentForm.receiptNumber || `REC-${Date.now()}`
      };

      // Usar el ID real del pago que viene del backend
      if (isAdmin) {
        await dispatch(registerAdminRentalPayment(selectedPayment.id, paymentData));
        dispatch(fetchAdminRentalPayments(contractId));
      } else {
        await dispatch(registerPayment(selectedPayment.id, paymentData));
        dispatch(fetchRentalPayments(contractId));
      }
      
      setSelectedPayment(null);
      setPaymentForm({
        amountPaid: '',
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: 'Transferencia',
        receiptNumber: '',
        notes: ''
      });
      setShowAddPayment(false);
      alert('Pago registrado exitosamente');
    } catch (error) {
      console.error('Error al registrar el pago:', error);
      alert('Error al registrar el pago: ' + (error.message || 'Error desconocido'));
    }
  };

  const handleGenerateSchedule = async () => {
    try {
      if (isAdmin) {
        const regenerate = window.confirm(
          '¿Regenerar cronograma existente? OK = regenerar, Cancelar = generar si no existe.'
        );
        await dispatch(generateAdminPaymentSchedule(contractId, regenerate));
        dispatch(fetchAdminRentalPayments(contractId));
      } else {
        await dispatch(generatePaymentSchedule(contractId));
        dispatch(fetchRentalPayments(contractId));
      }
      alert('Cronograma de pagos generado exitosamente');
    } catch (error) {
      console.error('Error al generar cronograma:', error);
      alert('Error al generar cronograma: ' + (error.message || 'Error desconocido'));
    }
  };

  const simulateAdjustment = async () => {
    try {
      const result = await dispatch(simulateRentalAdjustment(contractId, adjustmentData));
      setSimulation(result.simulation || result);
    } catch (error) {
      console.error('Error al simular ajuste:', error);
      alert('Error al simular el ajuste: ' + (error.message || 'Error desconocido'));
    }
  };

  const applyAdjustment = async () => {
    try {
      const adjustmentPayload = {
        adjustmentType: simulation.adjustment.adjustmentType,
        percentage: simulation.adjustment.percentage,
        indexValue: simulation.adjustment.indexValue,
        appliedFrom: simulation.adjustment.appliedFrom,
        legalBasis: `Ajuste ${simulation.adjustment.adjustmentType} - ${simulation.adjustment.percentage}%`,
        notes: `Aplicado después de simulación. Afecta ${simulation.impact.affectedPayments} cuotas.`
      };

      const response = await fetch(`/api/rentals/admin/${contractId}/adjustments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(adjustmentPayload)
      });

      if (response.ok) {
        alert('Ajuste aplicado exitosamente');
        dispatch(fetchRentalPayments(contractId));
        setSimulation(null);
        setShowAdjustmentSimulator(false);
      } else {
        alert('Error al aplicar el ajuste');
      }
    } catch (error) {
      console.error('Error al aplicar ajuste:', error);
      alert('Error al aplicar el ajuste');
    }
  };

  const openReceiptModal = (payment) => {
    setReceiptData(payment);
    // Buscar inquilino principal desde rental.tenants
    const mainTenant = rental?.tenants?.find(t => t.tenantType === 'main');
    setReceiptTenantName(mainTenant?.fullName || '');
    setShowReceiptModal(true);
  };

  const closeReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptData(null);
    setReceiptDevMode(false);
    setReceiptPreviewScale(1);
  };

  useEffect(() => {
    if (!showReceiptModal) return;

    const updatePreviewScale = () => {
      const viewport = receiptPreviewViewportRef.current;
      if (!viewport) return;

      const receiptNode = receiptRef.current;
      const baseWidth = receiptNode?.offsetWidth || 642;
      const baseHeight = receiptNode?.offsetHeight || 831;
      const availableWidth = Math.max(viewport.clientWidth - 24, 0);
      const availableHeight = Math.max(viewport.clientHeight - 24, 0);
      const fitScale = Math.min(availableWidth / baseWidth, availableHeight / baseHeight, 1);
      setReceiptPreviewScale(fitScale > 0 ? fitScale : 1);
    };

    updatePreviewScale();
    window.addEventListener('resize', updatePreviewScale);

    return () => {
      window.removeEventListener('resize', updatePreviewScale);
    };
  }, [showReceiptModal]);

  const generateReceiptPdf = async () => {
    if (!receiptRef.current) return;
    let exportHost = null;
    try {
      const sourceNode = receiptRef.current;

      // Clon limpio fuera del modal para evitar efectos de transform/scale del preview
      exportHost = document.createElement('div');
      exportHost.style.position = 'fixed';
      exportHost.style.left = '-10000px';
      exportHost.style.top = '0';
      exportHost.style.background = '#ffffff';
      exportHost.style.padding = '0';
      exportHost.style.margin = '0';
      exportHost.style.zIndex = '-1';

      const exportNode = sourceNode.cloneNode(true);
      exportNode.style.transform = 'none';
      exportNode.style.width = `${sourceNode.offsetWidth}px`;
      exportNode.style.height = `${sourceNode.offsetHeight}px`;
      exportNode.classList.remove(style.receiptDevMode);

      exportHost.appendChild(exportNode);
      document.body.appendChild(exportHost);

      // Capturar a alta resolución
      const scale = 2;
      const canvas = await html2canvas(exportNode, {
        scale,
        useCORS: true,
        backgroundColor: '#ffffff',
        width: sourceNode.offsetWidth,
        height: sourceNode.offsetHeight,
        windowWidth: sourceNode.offsetWidth,
        windowHeight: sourceNode.offsetHeight,
        scrollX: 0,
        scrollY: 0
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.98);

      // Generar PDF con el mismo tamaño real del remito (17 x 22 cm)
      const pdf = new jsPDF({ unit: 'mm', format: [RECEIPT_MM.w, RECEIPT_MM.h] });
      pdf.addImage(imgData, 'JPEG', 0, 0, RECEIPT_MM.w, RECEIPT_MM.h);
      pdf.save(`${receiptData?.receiptNumber || 'recibo'}_${receiptData?.paymentNumber || ''}.pdf`);
    } catch (err) {
      console.error('Error generando PDF:', err);
      alert('Error al generar el PDF del recibo');
    } finally {
      if (exportHost && exportHost.parentNode) {
        exportHost.parentNode.removeChild(exportHost);
      }
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  const formatMonthYear = (dateString) => {
    if (!dateString) return '';
    const normalizedDate = String(dateString).includes('T') ? dateString : `${dateString}T00:00:00`;
    return new Date(normalizedDate).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
  };

  const getStatusInfo = (paymentStatus) => {
    switch (paymentStatus) {
      case 'paid':
        return { color: 'success', text: 'Pagado', icon: FaCheckCircle };
      case 'overdue':
        return { color: 'danger', text: 'Vencido', icon: FaExclamationTriangle };
      case 'pending':
        return { color: 'warning', text: 'Pendiente', icon: FaClock };
      default:
        return { color: 'info', text: paymentStatus, icon: FaClock };
    }
  };

  const filteredPayments = payments.filter(payment => {
    if (filterStatus === 'all') return true;
    return payment.paymentStatus === filterStatus;
  });

  const paymentStats = {
    total: payments.length,
    paid: payments.filter(p => p.paymentStatus === 'paid').length,
    overdue: payments.filter(p => p.paymentStatus === 'overdue').length,
    pending: payments.filter(p => p.paymentStatus === 'pending').length,
    totalAmount: payments.filter(p => p.paymentStatus === 'paid').reduce((sum, p) => sum + parseFloat(p.amountPaid || 0), 0), // Usar amountPaid real
    paidAmount: payments.filter(p => p.paymentStatus === 'paid').reduce((sum, p) => sum + parseFloat(p.amountPaid || 0), 0),
    overdueAmount: payments.filter(p => p.paymentStatus === 'overdue').reduce((sum, p) => sum + parseFloat(p.totalAmount || 0), 0),
    pendingAmountWithoutAdjustments: payments.filter(p => p.paymentStatus === 'pending').reduce((sum, p) => {
      // Para pendientes, usar el valor base sin futuros ajustes
      const baseAmount = parseFloat(rental?.currentRentValue || 0) + parseFloat(rental?.expensesAmount || 0);
      return sum + baseAmount;
    }, 0),
    adjustmentsCount: payments.filter(p => 
      p.paymentStatus === 'paid' && 
      (parseFloat(p.amountPaid) !== parseFloat(p.totalAmount) || 
       isAdjustmentMonth(p.paymentNumber, rental?.contractStart, rental?.adjustmentInfo?.adjustmentFrequency || rental?.adjustmentFrequency))
    ).length, // Contar pagos con diferencia de monto O meses de ajuste
    scheduledAdjustments: payments.filter(p => 
      isAdjustmentMonth(p.paymentNumber, rental?.contractStart, rental?.adjustmentInfo?.adjustmentFrequency || rental?.adjustmentFrequency)
    ).length // Total de ajustes programados
  };

  if (loading) {
    return (
      <div className={style.container}>
        <div className={style.loading}>
          <p>Cargando pagos del contrato...</p>
        </div>
      </div>
    );
  }



  if (error) {
    return (
      <div className={style.container}>
        <div className={style.notFound}>
          <h2>Error al cargar los pagos</h2>
          <p>{error}</p>
          <Link to="/alquileres" className={style.backButton}>
            <FaArrowLeft /> Volver a Alquileres
          </Link>
        </div>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className={style.container}>
        <div className={style.notFound}>
          <h2>Contrato no encontrado</h2>
          <p>No se pudo encontrar el contrato con ID: {contractId}</p>
          <Link to="/alquileres" className={style.backButton}>
            <FaArrowLeft /> Volver a Alquileres
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={style.container}>
      {/* Header */}
      <div className={style.header}>
        <div className={style.headerLeft}>
          <button onClick={() => navigate(getDetailPath(contractId, type))} className={style.backBtn}>
            <FaArrowLeft /> Volver al Contrato
          </button>
          <div>
            <h1 className={style.title}>
              <span
                style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: 999,
                  fontSize: '0.7rem',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  background: isAdmin ? '#fef3c7' : '#dbeafe',
                  color: isAdmin ? '#92400e' : '#1e40af',
                  marginRight: 10,
                  verticalAlign: 'middle'
                }}
                title={typeLabel(type)}
              >
                {isAdmin ? 'Solo Admin' : 'Regular'}
              </span>
              Gestión de Pagos
            </h1>
            <p className={style.subtitle}>
              {rental.street} {rental.number}, {rental.locality}
            </p>
          </div>
        </div>
        
        <div className={style.headerActions}>
          <button 
            onClick={() => setShowAddPayment(true)}
            className={style.actionBtn}
          >
            <FaPlus /> Registrar Pago
          </button>
          <button 
            onClick={() => {
              if (isAdmin) {
                navigate(getDetailPath(contractId, type, 'ajustes'));
              } else {
                setShowAdjustmentSimulator(true);
              }
            }}
            className={`${style.actionBtn} ${style.simulateBtn}`}
          >
            <FaDollarSign /> {isAdmin ? 'Gestionar Ajustes' : 'Simular Ajustes'}
          </button>
          <button className={style.actionBtn}>
            <FaDownload /> Exportar
          </button>
          <button 
            onClick={() => {
              if (window.confirm('¿Está seguro que desea cancelar este contrato? Esta acción no se puede deshacer.')) {
                // Aquí iría la lógica de cancelación
                alert('Funcionalidad de cancelación pendiente de implementar');
              }
            }}
            className={`${style.actionBtn} ${style.cancelBtn}`}
          >
            <FaExclamationTriangle /> Cancelar Contrato
          </button>
        </div>
      </div>

      {/* Estadísticas */}
      <div className={style.statsGrid}>
        <div className={style.statCard}>
          <div className={style.statIcon}>
            <FaDollarSign />
          </div>
          <div className={style.statContent}>
            <h3>Total Cobrado</h3>
            <p className={style.statAmount}>{formatCurrency(paymentStats.totalAmount)}</p>
            <span className={style.statSubtext}>{paymentStats.paid} cuotas pagadas</span>
            {paymentStats.adjustmentsCount > 0 && (
              <span className={style.adjustmentInfo}>
                + {paymentStats.adjustmentsCount} ajustes aplicados de {paymentStats.scheduledAdjustments} programados
              </span>
            )}
            {paymentStats.scheduledAdjustments > 0 && paymentStats.adjustmentsCount === 0 && (
              <span className={style.adjustmentInfo}>
                {paymentStats.scheduledAdjustments} ajustes programados cada {rental?.adjustmentInfo?.adjustmentFrequency || rental?.adjustmentFrequency || 12} meses
              </span>
            )}
          </div>
        </div>

        <div className={style.statCard}>
          <div className={`${style.statIcon} ${style.success}`}>
            <FaCheckCircle />
          </div>
          <div className={style.statContent}>
            <h3>Pagado</h3>
            <p className={style.statAmount}>{formatCurrency(paymentStats.paidAmount)}</p>
            <span className={style.statSubtext}>{paymentStats.paid} cuotas</span>
          </div>
        </div>

        <div className={style.statCard}>
          <div className={`${style.statIcon} ${style.danger}`}>
            <FaExclamationTriangle />
          </div>
          <div className={style.statContent}>
            <h3>Vencido</h3>
            <p className={style.statAmount}>{formatCurrency(paymentStats.overdueAmount)}</p>
            <span className={style.statSubtext}>{paymentStats.overdue} cuotas</span>
          </div>
        </div>

        <div className={style.statCard}>
          <div className={`${style.statIcon} ${style.warning}`}>
            <FaClock />
          </div>
          <div className={style.statContent}>
            <h3>Pendiente</h3>
            <p className={style.statAmount}>
              {formatCurrency(paymentStats.pendingAmountWithoutAdjustments)}
            </p>
            <span className={style.statSubtext}>{paymentStats.pending} cuotas</span>
            <span className={style.pendingNote}>*Sin ajustes futuros</span>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className={style.filters}>
        <div className={style.filterGroup}>
          <FaFilter />
          <span>Filtrar por estado:</span>
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className={style.filterSelect}
          >
            <option value="all">Todos</option>
            <option value="paid">Pagados</option>
            <option value="overdue">Vencidos</option>
            <option value="pending">Pendientes</option>
          </select>
        </div>
      </div>

      {/* Lista de Pagos */}
      <div className={style.paymentsSection}>
        {payments.length === 0 ? (
          <div className={style.noPayments}>
            <div className={style.noPaymentsIcon}>
              <FaCalendarAlt />
            </div>
            <h2>No hay pagos registrados</h2>
            <p>Este contrato aún no tiene un cronograma de pagos generado.</p>
            <p>Genera automáticamente todas las cuotas mensuales del contrato.</p>
            
            <div className={style.contractInfo}>
              {contractInfo?.ownerName && (
                <div className={style.infoItem}>
                  <strong>Propietario:</strong> {contractInfo.ownerName}
                </div>
              )}
              <div className={style.infoItem}>
                <strong>Duración del contrato:</strong> {rental?.contractDuration || 0} meses
              </div>
              <div className={style.infoItem}>
                <strong>Valor mensual:</strong> {formatCurrency(rental?.currentRentValue)} {rental?.currency}
              </div>
              <div className={style.infoItem}>
                <strong>Día de vencimiento:</strong> {rental?.paymentDueDay} de cada mes
              </div>
              <div className={style.infoItem}>
                <strong>Ajustes:</strong> {rental?.adjustmentType} cada {rental?.adjustmentFrequency} meses
              </div>
            </div>
            
            <button 
              onClick={handleGenerateSchedule}
              className={style.generateButton}
            >
              <FaPlus /> Generar Cronograma de Pagos
            </button>
          </div>
        ) : (
          <>
            <h2 className={style.sectionTitle}>
              Historial de Pagos ({filteredPayments.length})
            </h2>
            
            <div className={style.paymentsList}>
              {filteredPayments.map((payment) => {
                const statusInfo = getStatusInfo(payment.paymentStatus);
                const StatusIcon = statusInfo.icon;
                
                // Determinar si es mes de ajuste
                const isAdjustmentPayment = isAdjustmentMonth(
                  payment.paymentNumber, 
                  rental?.contractStart, 
                  rental?.adjustmentInfo?.adjustmentFrequency || rental?.adjustmentFrequency
                );
                
                // Calcular porcentaje de aumento si está pagado y hay diferencia
                const adjustmentPercentage = payment.paymentStatus === 'paid' 
                  ? calculateAdjustmentPercentage(payment.rentAmount, payment.amountPaid)
                  : 0;
                
                return (
                  <div 
                    key={payment.id} 
                    className={`${style.paymentCard} ${style[statusInfo.color]} ${isAdjustmentPayment ? style.adjustmentMonth : ''}`}
                  >
                    <div className={style.paymentHeader}>
                      <div className={style.paymentInfo}>
                        <h4 className={style.paymentMonth}>
                          Cuota {payment.paymentNumber} - {new Date(payment.periodStart + 'T00:00:00').toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })}
                          {isAdjustmentPayment && (
                            <span className={style.adjustmentBadge}>
                              <FaDollarSign /> AJUSTE {rental?.adjustmentInfo?.adjustmentType || rental?.adjustmentType || 'PROGRAMADO'} (cada {rental?.adjustmentInfo?.adjustmentFrequency || rental?.adjustmentFrequency || 12} meses)
                            </span>
                          )}
                          {payment.paymentStatus === 'paid' && adjustmentPercentage > 0 && (
                            <span className={style.increaseBadge}>
                              +{adjustmentPercentage}%
                            </span>
                          )}
                        </h4>
                        <p className={style.paymentAmount}>
                          {payment.paymentStatus === 'paid' 
                            ? formatCurrency(payment.amountPaid)
                            : formatCurrency(payment.totalAmount)}
                          {payment.paymentStatus === 'paid' && 
                           parseFloat(payment.amountPaid) !== parseFloat(payment.totalAmount) && (
                            <span className={style.adjustmentIndicator}>
                              {parseFloat(payment.amountPaid) > parseFloat(payment.totalAmount) 
                                ? ' ↗️ AUMENTO' 
                                : ' ↙️ DESCUENTO'}
                            </span>
                          )}
                        </p>
                      </div>
                      
                      <div className={`${style.paymentStatus} ${style[statusInfo.color]}`}>
                        <StatusIcon />
                        <span>{statusInfo.text}</span>
                      </div>
                    </div>
                    
                    <div className={style.paymentDetails}>
                      <div className={style.paymentDetail}>
                        <label>Vencimiento:</label>
                        <span>{new Date(payment.dueDate + 'T00:00:00').toLocaleDateString('es-AR')}</span>
                      </div>
                      
                      <div className={style.paymentDetail}>
                        <label>Alquiler:</label>
                        <span>
                          {payment.paymentStatus === 'paid' 
                            ? formatCurrency(payment.amountPaid) 
                            : formatCurrency(payment.rentAmount)}
                          {payment.paymentStatus === 'paid' && 
                           parseFloat(payment.amountPaid) !== parseFloat(payment.rentAmount) && (
                            <small style={{color: '#10b981', marginLeft: '8px'}}>
                              (Orig: {formatCurrency(payment.rentAmount)})
                            </small>
                          )}
                        </span>
                      </div>
                      
                      <div className={style.paymentDetail}>
                        <label>Expensas:</label>
                        <span>{formatCurrency(payment.expensesAmount)}</span>
                      </div>
                      
                      {payment.paymentDate && (
                        <div className={style.paymentDetail}>
                          <label>Fecha de Pago:</label>
                          <span>{new Date(payment.paymentDate + 'T00:00:00').toLocaleDateString('es-AR')}</span>
                        </div>
                      )}
                      
                      {payment.receiptNumber && (
                        <div className={style.paymentDetail}>
                          <label>N° Recibo:</label>
                          <span>{payment.receiptNumber}</span>
                        </div>
                      )}
                      
                      {payment.daysOverdue > 0 && (
                        <div className={style.paymentDetail}>
                          <label>Días de Atraso:</label>
                          <span style={{color: '#ef4444', fontWeight: 'bold'}}>{payment.daysOverdue} días</span>
                        </div>
                      )}
                      
                      {parseFloat(payment.lateInterest) > 0 && (
                        <div className={style.paymentDetail}>
                          <label>Interés por Mora:</label>
                          <span style={{color: '#ef4444', fontWeight: 'bold'}}>{formatCurrency(payment.lateInterest)}</span>
                        </div>
                      )}
                      
                      {isAdjustmentPayment && (
                        <div className={style.paymentDetail}>
                          <label>Información de Ajuste:</label>
                          <div className={style.adjustmentInfo}>
                            <span className={style.adjustmentType}>
                              📊 Tipo: {rental?.adjustmentInfo?.adjustmentType || rental?.adjustmentType || 'IPC'}
                            </span>
                            <span className={style.adjustmentFreq}>
                              📅 Frecuencia: cada {rental?.adjustmentInfo?.adjustmentFrequency || rental?.adjustmentFrequency || 12} meses
                            </span>
                            {payment.paymentStatus === 'paid' && adjustmentPercentage > 0 && (
                              <span className={style.adjustmentApplied}>
                                ✅ Aumento aplicado: +{adjustmentPercentage}%
                              </span>
                            )}
                            {payment.paymentStatus === 'pending' && (
                              <span className={style.adjustmentPending}>
                                ⏳ Ajuste pendiente de aplicar
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {payment.adjustmentApplied && (
                        <div className={style.paymentDetail}>
                          <label>Ajuste Aplicado:</label>
                          <span style={{color: '#10b981', fontWeight: 'bold'}}>✓ Ajuste ICL</span>
                        </div>
                      )}
                    </div>
                    
                    {payment.paymentStatus === 'pending' && (
                      <div className={style.pendingActions}>
                        <button 
                          className={`${style.actionBtn} ${style.payBtn}`}
                          onClick={() => {
                            setSelectedPayment(payment);
                            setPaymentForm({
                              ...paymentForm,
                              amountPaid: payment.totalAmount,
                              notes: `Pago cuota #${payment.paymentNumber}`
                            });
                            setShowAddPayment(true);
                          }}
                        >
                          Registrar Pago
                        </button>
                        <button className={style.actionBtn}>Enviar Recordatorio</button>
                      </div>
                    )}
                    
                    {payment.paymentStatus === 'overdue' && (
                      <div className={style.overdueActions}>
                        <button 
                          className={style.actionBtn}
                          onClick={() => {
                            setSelectedPayment(payment);
                            setPaymentForm({
                              ...paymentForm,
                              amountPaid: payment.totalAmount,
                              paymentMethod: 'Manual',
                              receiptNumber: `MAN-${Date.now()}`,
                              notes: `Pago atrasado cuota #${payment.paymentNumber}`
                            });
                            setShowAddPayment(true);
                          }}
                        >
                          Marcar como Pagado
                        </button>
                        <button className={style.actionBtn}>Enviar Recordatorio</button>
                      </div>
                    )}
                    
                    {payment.paymentStatus === 'paid' && (
                      <div className={style.paidActions}>
                        <button className={style.actionBtn} onClick={() => openReceiptModal(payment)}>Ver Recibo</button>
                        <button className={style.actionBtn} onClick={() => openReceiptModal(payment)}>Descargar</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Modal para registrar pago */}
      {showAddPayment && (
        <div className={style.modal}>
          <div className={style.modalContent}>
            <div className={style.modalHeader}>
              <h3>Registrar Pago</h3>
              <button 
                className={style.closeButton}
                onClick={() => {
                  setShowAddPayment(false);
                  setSelectedPayment(null);
                }}
              >
                ×
              </button>
            </div>
            
            <form className={style.paymentForm} onSubmit={handleRegisterPayment}>
              <div className={style.formGroup}>
                <label>Cuota a Pagar *</label>
                <select 
                  className={style.input}
                  value={selectedPayment?.id || ''}
                  onChange={(e) => {
                    const payment = payments.find(p => String(p.id) === e.target.value);
                    setSelectedPayment(payment);
                    if (payment) {
                      setPaymentForm({
                        ...paymentForm,
                        amountPaid: payment.totalAmount,
                        notes: `Pago cuota #${payment.paymentNumber}`
                      });
                    }
                  }}
                  required
                >
                  <option value="">Seleccionar cuota...</option>
                  {payments.filter(p => p.paymentStatus === 'pending' || p.paymentStatus === 'overdue').map(payment => {
                    const dueDate = new Date(payment.dueDate + 'T00:00:00');
                    const monthName = dueDate.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
                    const dayMonth = dueDate.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
                    
                    return (
                      <option key={payment.id} value={payment.id}>
                        Cuota #{payment.paymentNumber} - {monthName} - Vence: {dayMonth} - {formatCurrency(payment.totalAmount)}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              {selectedPayment && (
                <div className={style.selectedPaymentInfo}>
                  <h4>Detalle de la Cuota Seleccionada</h4>
                  <div className={style.paymentDetail}>
                    <span><strong>Período:</strong> {new Date(selectedPayment.periodStart + 'T00:00:00').toLocaleDateString('es-AR')} - {new Date(selectedPayment.periodEnd + 'T00:00:00').toLocaleDateString('es-AR')}</span>
                  </div>
                  <div className={style.paymentDetail}>
                    <span><strong>Fecha de vencimiento:</strong> {new Date(selectedPayment.dueDate + 'T00:00:00').toLocaleDateString('es-AR')}</span>
                  </div>
                  <div className={style.paymentDetail}>
                    <span><strong>Alquiler:</strong> {formatCurrency(selectedPayment.rentAmount)}</span>
                  </div>
                  <div className={style.paymentDetail}>
                    <span><strong>Expensas:</strong> {formatCurrency(selectedPayment.expensesAmount)}</span>
                  </div>
                  <div className={style.paymentDetail}>
                    <span><strong>Total:</strong> {formatCurrency(selectedPayment.totalAmount)}</span>
                  </div>
                </div>
              )}
              
              <div className={style.formGroup}>
                <label>Monto Pagado *</label>
                <input 
                  type="number" 
                  className={style.input}
                  value={paymentForm.amountPaid}
                  onChange={(e) => setPaymentForm({...paymentForm, amountPaid: e.target.value})}
                  placeholder="Monto a pagar"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
              
              <div className={style.formGroup}>
                <label>Fecha de Pago *</label>
                <input 
                  type="date" 
                  className={style.input}
                  value={paymentForm.paymentDate}
                  onChange={(e) => setPaymentForm({...paymentForm, paymentDate: e.target.value})}
                  required
                />
              </div>
              
              <div className={style.formGroup}>
                <label>Método de Pago *</label>
                <select 
                  className={style.input}
                  value={paymentForm.paymentMethod}
                  onChange={(e) => setPaymentForm({...paymentForm, paymentMethod: e.target.value})}
                  required
                >
                  <option value="Transferencia">Transferencia Bancaria</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Cheque">Cheque</option>
                  <option value="Débito">Tarjeta de Débito</option>
                  <option value="Credito">Tarjeta de Crédito</option>
                </select>
              </div>
              
              <div className={style.formGroup}>
                <label>Número de Recibo</label>
                <input 
                  type="text" 
                  className={style.input}
                  value={paymentForm.receiptNumber}
                  onChange={(e) => setPaymentForm({...paymentForm, receiptNumber: e.target.value})}
                  placeholder="Número de recibo (opcional)"
                />
              </div>
              
              <div className={style.formGroup}>
                <label>Notas</label>
                <textarea 
                  className={style.input}
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm({...paymentForm, notes: e.target.value})}
                  placeholder="Notas adicionales (opcional)"
                  rows="3"
                />
              </div>
              
              <div className={style.modalActions}>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowAddPayment(false);
                    setSelectedPayment(null);
                  }}
                  className={style.cancelBtn}
                >
                  Cancelar
                </button>
                <button 
                  type="submit" 
                  className={style.saveBtn}
                  disabled={!selectedPayment || !paymentForm.amountPaid}
                >
                  Registrar Pago
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de vista previa / descarga del recibo */}
      {showReceiptModal && (
        <div className={style.modal}>
          <div className={`${style.modalContent} ${style.receiptModalContent}`}>
            <div className={style.modalHeader}>
              <h3>Recibo — Cuota #{receiptData?.paymentNumber}</h3>
              <div className={style.receiptHeaderActions}>
                {isDev && (
                  <button
                    type="button"
                    className={`${style.devPreviewBtn} ${receiptDevMode ? style.activeDevPreview : ''}`}
                    onClick={() => setReceiptDevMode(!receiptDevMode)}
                  >
                    {receiptDevMode ? 'Desarrollo: ON' : 'Desarrollo: OFF'}
                  </button>
                )}
                <button className={style.closeButton} onClick={closeReceiptModal}>×</button>
              </div>
            </div>

            <div className={style.receiptTenantField}>
              <label>Nombre del inquilino en el recibo:</label>
              <input
                type="text"
                className={style.input}
                value={receiptTenantName}
                onChange={(e) => setReceiptTenantName(e.target.value)}
                placeholder="Nombre del inquilino"
              />
            </div>

            <div className={`${style.modalBody} ${style.receiptModalBody}`}>
              <div className={style.receiptPreviewWrap} ref={receiptPreviewViewportRef}>
                <div className={style.receiptScaleLayer} style={{ transform: `scale(${receiptPreviewScale})` }}>
                <div ref={receiptRef} className={`${style.receiptSheet} ${receiptDevMode ? style.receiptDevMode : ''}`}>
                <div className={style.receiptHeader}>
                  <div className={style.receiptLogo}>
                    <img src={logo} alt="By Raices" className={style.logoImg} />
                  </div>

                  <div className={style.receiptCompany}>
                    <div className={style.companyName}>By Raíces</div>
                    <div className={style.companyAddr}>Av Peltier 50 - Piso 1 - Oficina 11 - Capital - Mendoza</div>
                    <div className={style.companyContact}>Tel. {'2612088110'} - www.byraices.com</div>
                  </div>

                    <div className={style.receiptXWrapper}>
                      <div className={style.receiptX}>X</div>
                    </div>

                    <div className={style.receiptRight}>
                    <div className={style.receiptTitle}>RECIBO</div>
                    <div className={style.receiptNumberBox}>N° {receiptData?.receiptNumber || `0000-000-${Date.now()%100000}`}</div>
                    <div className={style.receiptDateBox}>{formatDate(receiptData?.paymentDate || new Date().toISOString())}</div>
                  </div>
                </div>

                <div className={style.receiptBody}>
                  <div className={style.fieldRow}>
                    <div className={style.fieldLabel}>SR.(ES) {receiptTenantName || ''}</div>
                  </div>

                  <div className={style.fieldRow}>
                    <div className={style.fieldLabel}>DOMICILIO</div>
                    <div className={style.fieldValue}>{rental?.street} {rental?.number}</div>
                  </div>

             
                  <div className={style.receiptLineRow}>
                    <span className={style.lineLabel}>Recibí (mos) la suma de pesos</span>
                    <span className={style.lineDots}></span>
                    <span className={style.lineValue}>{formatCurrency(receiptData?.amountPaid || receiptData?.totalAmount)}</span>
                  </div>

                  <div className={style.receiptLineRow}>
                    <span className={style.lineLabel}>en concepto de</span>
                    <span className={style.lineDots}></span>
                    <span className={style.lineValue}>Alquiler - Cuota #{receiptData?.paymentNumber}</span>
                  </div>

                  <div className={style.receiptLineRow}>
                    <span className={style.lineLabel}>correspondiente al mes de</span>
                    <span className={style.lineDots}></span>
                    <span className={style.lineValue}>{formatMonthYear(receiptData?.periodStart || receiptData?.dueDate || new Date().toISOString())}</span>
                  </div>

                  <div className={style.receiptLineRow}>
                    <span className={style.lineLabel}>que vence el día</span>
                    <span className={style.lineDots}></span>
                    <span className={style.lineValue}>{formatDate(receiptData?.dueDate || new Date().toISOString())}</span>
                  </div>

                  <div className={style.receiptLineRow}>
                    <span className={style.lineLabel}>entrega a cuenta</span>
                    <span className={style.lineDots}></span>
                    <span className={style.lineValue}>{receiptData?.paymentMethod || '-'}</span>
                  </div>

                  <div className={style.receiptLineRow}>
                    <span className={style.lineLabel}>cheque N° / c-Banco / fecha</span>
                    <span className={style.lineDots}></span>
                    <span className={style.lineValue}>-</span>
                  </div>

                  <div className={style.receiptNotesBlock}>
                    <div className={style.notesTitle}>Observaciones</div>
                    <div className={style.notesArea}>{receiptData?.notes || ''}</div>
                  </div>
                </div>

                <div className={style.receiptFooter}>
                  <div className={style.totalBox}>TOTAL<br/><span>{formatCurrency(receiptData?.amountPaid || receiptData?.totalAmount)}</span></div>
                  <div className={style.signatureBox}>
                    <div className={style.signatureLine}></div>
                    <div className={style.signatureLabel}>FIRMA</div>
                  </div>
                </div>
                </div>
                </div>
              </div>
            </div>

            <div className={style.modalActions}>
              <button type="button" className={style.cancelBtn} onClick={closeReceiptModal}>Cerrar</button>
              <button type="button" className={style.saveBtn} onClick={generateReceiptPdf}>Descargar PDF</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para simulador de ajustes */}
      {showAdjustmentSimulator && (
        <div className={style.modal}>
          <div className={style.modalContent}>
            <div className={style.modalHeader}>
              <h3>Simulador de Ajustes</h3>
              <button 
                className={style.closeButton}
                onClick={() => {
                  setShowAdjustmentSimulator(false);
                  setSimulation(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className={style.modalBody}>
              {!simulation ? (
                <div className={style.simulatorForm}>
                  <div className={style.formGroup}>
                    <label>Tipo de Ajuste:</label>
                    <select 
                      value={adjustmentData.adjustmentType}
                      onChange={(e) => setAdjustmentData({...adjustmentData, adjustmentType: e.target.value})}
                      className={style.input}
                    >
                      <option value="ICL">ICL (Índice Contratos Locación)</option>
                      <option value="IPC">IPC (Índice Precios Consumidor)</option>
                      <option value="porcentual">Porcentual</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>

                  {adjustmentData.adjustmentType !== 'manual' && (
                    <div className={style.formGroup}>
                      <label>Porcentaje de Ajuste:</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={adjustmentData.percentage}
                        onChange={(e) => setAdjustmentData({...adjustmentData, percentage: e.target.value})}
                        className={style.input}
                        placeholder="Ej: 15.5"
                      />
                    </div>
                  )}

                  {adjustmentData.adjustmentType === 'manual' && (
                    <div className={style.formGroup}>
                      <label>Nuevo Monto Mensual:</label>
                      <input 
                        type="number"
                        value={adjustmentData.newAmount}
                        onChange={(e) => setAdjustmentData({...adjustmentData, newAmount: e.target.value})}
                        className={style.input}
                        placeholder="Ej: 180000"
                      />
                    </div>
                  )}

                  {(adjustmentData.adjustmentType === 'ICL' || adjustmentData.adjustmentType === 'IPC') && (
                    <div className={style.formGroup}>
                      <label>Mes del Índice:</label>
                      <input 
                        type="month"
                        value={adjustmentData.indexMonth}
                        onChange={(e) => setAdjustmentData({...adjustmentData, indexMonth: e.target.value})}
                        className={style.input}
                      />
                    </div>
                  )}

                  <div className={style.currentInfo}>
                    <h4>Información Actual del Contrato</h4>
                    <p><strong>Valor actual:</strong> {formatCurrency(rental?.currentRentValue)} {rental?.currency}</p>
                    <p><strong>Próximo ajuste programado:</strong> {rental?.nextAdjustmentDate ? formatDate(rental.nextAdjustmentDate) : 'No programado'}</p>
                    <p><strong>Frecuencia de ajustes:</strong> cada {rental?.adjustmentFrequency || 12} meses</p>
                  </div>
                </div>
              ) : (
                <div className={style.simulationResults}>
                  <h4>Resultados de la Simulación</h4>
                  
                  <div className={style.resumen}>
                    <div className={style.resumenItem}>
                      <strong>Monto actual:</strong> {formatCurrency(simulation.calculations.previousAmount)}
                    </div>
                    <div className={style.resumenItem}>
                      <strong>Porcentaje de ajuste:</strong> {simulation.calculations.adjustmentPercentage}%
                    </div>
                    <div className={style.resumenItem}>
                      <strong>Nuevo monto:</strong> {formatCurrency(simulation.calculations.newAmount)}
                    </div>
                    <div className={style.resumenItem}>
                      <strong>Diferencia mensual:</strong> +{formatCurrency(simulation.calculations.monthlyDifference)}
                    </div>
                  </div>

                  <div className={style.impacto}>
                    <h5>Impacto del Ajuste</h5>
                    <div className={style.impactoGrid}>
                      <div className={style.impactoItem}>
                        <span>Cuotas afectadas:</span>
                        <span>{simulation.impact.affectedPayments}</span>
                      </div>
                      <div className={style.impactoItem}>
                        <span>Aumento total anual:</span>
                        <span>{formatCurrency(simulation.impact.annualIncrease)}</span>
                      </div>
                      <div className={style.impactoItem}>
                        <span>Aumento total restante:</span>
                        <span>{formatCurrency(simulation.impact.totalIncrease)}</span>
                      </div>
                    </div>
                  </div>

                  {simulation.preview && simulation.preview.length > 0 && (
                    <div className={style.preview}>
                      <h5>Vista Previa de Cuotas Afectadas</h5>
                      <div className={style.previewList}>
                        {simulation.preview.slice(0, 5).map(payment => (
                          <div key={payment.paymentNumber} className={style.previewItem}>
                            <span>Cuota {payment.paymentNumber}</span>
                            <span>{formatCurrency(payment.oldAmount)} → {formatCurrency(payment.newAmount)}</span>
                            <span>{formatDate(payment.dueDate)}</span>
                          </div>
                        ))}
                        {simulation.preview.length > 5 && (
                          <div className={style.previewMore}>
                            ... y {simulation.preview.length - 5} cuotas más
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            <div className={style.modalActions}>
              {!simulation ? (
                <>
                  <button 
                    type="button" 
                    onClick={() => setShowAdjustmentSimulator(false)}
                    className={style.cancelBtn}
                  >
                    Cancelar
                  </button>
                  <button 
                    onClick={simulateAdjustment}
                    className={style.simulateBtn}
                    disabled={!adjustmentData.percentage && !adjustmentData.newAmount}
                  >
                    Simular Ajuste
                  </button>
                </>
              ) : (
                <>
                  <button 
                    type="button" 
                    onClick={() => setSimulation(null)}
                    className={style.cancelBtn}
                  >
                    Nueva Simulación
                  </button>
                  <button 
                    onClick={applyAdjustment}
                    className={style.saveBtn}
                  >
                    Aplicar Este Ajuste
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RentalPayments;